import { useEffect, useState } from 'react';
import { Button, Card, CardHeader, Checkbox, CircularProgress, Divider, Grid, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { makeStyles } from "@mui/styles";

import { useDispatch, useSelector } from 'react-redux';
import { getGroupWellForecast, selectGroupWells } from '../reducers/groupWellSlice';
import { FixedSizeList } from 'react-window';

import uuid from 'react-uuid';

function not(a, b) {
  return a.filter((value) => b.indexOf(value) === -1);
};

function intersection(a, b) {
  return a.filter((value) => b.indexOf(value) !== -1);
};

function union(a, b) {
  return [...a, ...not(b, a)];
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
    marginTop: 10,
    width: '100%',
    position: 'relative'
  },
  listHeader: {
    position: 'fixed'
  },
  listContainer: {
    width: '45%',
  },
  list: {
    maxHeight: 300,
    minHeight: 300,
    overflow: 'auto',
  }
}));

export const EconomicsGroupWellsTransferList = ({ allGroupWellIds, groupWellIds, setGroupWellIds, isLoadingData, setIsLoadingData, userEmail }) => {

  const groupWells = useSelector(selectGroupWells);
  const dispatch = useDispatch();

  const classes = useStyles();
  const [checked, setChecked] = useState([]);
  const [left, setLeft] = useState(allGroupWellIds);

  useEffect(() => {
    setLeft(not(allGroupWellIds, groupWellIds));
  }, [allGroupWellIds, groupWellIds]);


  const leftChecked = intersection(checked, left);
  const groupWellIdsChecked = intersection(checked, groupWellIds);

  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    };

    setChecked(newChecked);

    const groupWell = groupWells?.byId[value];

    if (!groupWell?.production && groupWell?.location && groupWell?.location?.length > 0) {
      importGroupWell(groupWell?.location, userEmail);
    };
  };

  async function importGroupWell(location, userEmail) {
    try {
      console.log(`Fetching group well from S3 ${location}...`);

      setIsLoadingData(true);

      const res = await fetch(location, {cache: "no-store"});
      const gw = await res.json();

      if (!gw) return;

      if (gw?.createdBy !== userEmail && !gw?.public) {
        console.log('Changing group well owner...');
        dispatch(getGroupWellForecast({...gw, id: uuid(), createdBy: userEmail, lastModified: new Date()})).then(() => setIsLoadingData(false));
      } else {
        dispatch(getGroupWellForecast({...gw, location})).then(() => setIsLoadingData(false));
      };

    } catch (error) {
      console.log(error);
      return;
    };
  };

  const numberOfChecked = (items) => intersection(checked, items).length;

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    };
  };

  const handleCheckedRight = () => {
    setGroupWellIds(() => groupWellIds.concat(leftChecked));
    setLeft(() => not(left, leftChecked));
    setChecked(() => not(checked, leftChecked));
  };

  const handleAllRight = () => {
    setGroupWellIds(() => groupWellIds.concat(left));
    setLeft([]);
  };

  const handleCheckedLeft = () => {
    setLeft(() => left.concat(groupWellIdsChecked));
    setGroupWellIds(() => not(groupWellIds, groupWellIdsChecked));
    setChecked(() => not(checked, groupWellIdsChecked));
  };

  const handleAllLeft = () => {
    setLeft(left.concat(groupWellIds));
    setGroupWellIds([]);
  };

  useEffect(() => {
    setLeft(allGroupWellIds);
  }, [allGroupWellIds]);

  function renderLeftSourceRow(props) {
    const { index, style } = props;
    return (
      <ListItem
        key={left[index]}
        style={{...style, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center'}}
        role="listitem"
        button
        onClick={handleToggle(left[index])}
        dense
      >
        <ListItemIcon>
          <Checkbox
            checked={checked.indexOf(left[index]) !== -1}
            tabIndex={-1}
            disableRipple
            color="primary"
          />
        </ListItemIcon>
        <ListItemText 
          primary={<Typography variant="body2" noWrap>{groupWells.byId[left[index]]?.name || left[index]}</Typography>}
          secondary={<Typography noWrap variant="caption" color="textSecondary">{!groupWells.byId[left[index]]?.production ? 'Click to download group well' : `Wells: ${groupWells.byId[left[index]]?.uwis?.length || 0}`}</Typography>}
        />              
      </ListItem>
    );
  };

  function renderRightSourceRow(props) {
    const { index, style } = props;
    return (
      <ListItem
        key={groupWellIds[index]}
        style={{...style, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center'}}
        role="listitem"
        button
        onClick={handleToggle(groupWellIds[index])}
        dense
      >
        <ListItemIcon>
          <Checkbox
            checked={checked.indexOf(groupWellIds[index]) !== -1}
            tabIndex={-1}
            disableRipple
            color="primary"
          />
        </ListItemIcon>
        <ListItemText 
          primary={<Typography variant="body2" noWrap>{groupWells?.byId[groupWellIds[index]]?.name || groupWellIds[index]}</Typography>} 
          secondary={<Typography noWrap variant="caption" color="textSecondary">{!groupWells.byId[groupWellIds[index]]?.production ? 'Click to download group well' : `Wells: ${groupWells?.byId[groupWellIds[index]]?.uwis?.length || 0}`}</Typography>}
        />
      </ListItem>
    );
  };

  const customList = (title, items, side) => (
    <Card style={{padding: 0, margin: 0, width: '100%'}}>
      <CardHeader
        avatar={
          <Checkbox
            onClick={handleToggleAll(items)}
            checked={numberOfChecked(items) === items.length && items.length !== 0}
            indeterminate={
              numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0
            }
            disabled={items.length === 0}
            color="primary"
          />
        }
        title={title}
        subheader={`${numberOfChecked(items)}/${items.length} selected`}
        style={{paddingBottom: 10}}
      />
      <Divider />
      <FixedSizeList
        height={300}
        width={'100%'}
        itemSize={54}
        itemCount={side === 'left' ? left?.length : groupWellIds?.length}
      >
        {side === 'left' ? renderLeftSourceRow : renderRightSourceRow}
      </FixedSizeList>
    </Card>
  );

  return (
    <Grid container spacing={2} justifyContent="center" alignItems="center" className={classes.root}>
      {isLoadingData ? 
        <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', height: 400}}>
          <Typography style={{marginBottom: 20}}>Loading group wells...</Typography>
          <CircularProgress></CircularProgress>
        </div>
        :
      <>
      <Grid item className={classes.listContainer}>{customList('All Group Wells', left, 'left')}</Grid>
      <Grid item>
        <Grid container direction="column" alignItems="center">
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleAllRight}
            disabled={left.length === 0}
          >
            ≫
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
          >
            &gt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={groupWellIdsChecked.length === 0}
          >
            &lt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleAllLeft}
            disabled={groupWellIds.length === 0}
          >
            ≪
          </Button>
        </Grid>
      </Grid>
      <Grid item className={classes.listContainer}>{customList('Economics Forecast', groupWellIds, 'right')}</Grid>
    </>}
    </Grid>
  );

  // FUNCTIONS
  // function handleDecreaseQuantity(e, groupWellId) {
  //   e.stopPropagation();
  //   let newEconomicsWells = {...economicsWells};
  //   economicsWells.groupWells.forEach((groupWell, i) => {
  //     if (groupWell.id === groupWellId && newEconomicsWells.groupWells[i].quantity - 1 > 0) {
  //       newEconomicsWells.groupWells[i].quantity -= 1;
  //     };
  //   });

  //   setEconomicsWells(() => newEconomicsWells);
  // };

  // function handleIncreaseQuantity(e, groupWellId) {
  //   e.stopPropagation();
  //   let newEconomicsWells = {...economicsWells};
  //   economicsWells.groupWells.forEach((groupWell, i) => {
  //     if (groupWell.id === groupWellId) {
  //       newEconomicsWells.groupWells[i].quantity += 1;
  //     };
  //   });

  //   setEconomicsWells(() => newEconomicsWells);
  // };
};
